import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  Injector,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { DialogService } from '@nrwl/dialog-service';
import { RxButtonComponent } from '@nrwl/rx-button';
import { of } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { RxIconComponent } from '../rx-icon/rx-icon.component';
import { RxRowComponent } from '../rx-row/rx-row.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RxScrollableComponent } from '@org/design-system';
// import { PerfectScrollbarComponent } from "ngx-perfect-scrollbar";

@Component({
  selector: 'nrwl-rx-dialog',
  templateUrl: './rx-dialog.component.html',
  styleUrls: ['./rx-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RxButtonComponent,
    RxIconComponent,
    RxRowComponent,
    MatDialogModule,
  ],
})
export class RxDialogComponent implements OnInit, AfterViewInit {
  public contentComponentRef: any;
  public submitText: string;
  public cancelText: string;
  public hideDialogClose = false;
  public disableScroll = false;

  @ViewChild('contentPlaceholder', { read: ViewContainerRef, static: true })
  contentPlaceholder: ViewContainerRef;
  // WARNING: MIGRATION
  // @ViewChild(PerfectScrollbarComponent, { static: true }) perfectScrollBar: PerfectScrollbarComponent;

  get pdf() {
    if (!this.data.payload) {
      return;
    }
    return this.data.payload.pages ? this.data.payload.pages : null;
  }

  get isValid() {
    if (!this.data.payload?.fields) {
      return true;
    }
    return this.contentComponentRef.instance.isValid();
  }

  get hideHeader() {
    return this.data.hideHeader;
  }

  get headerClasses() {
    return this.data.headerClasses || '';
  }

  get bodyClasses() {
    return this.data.bodyClasses || '';
  }

  get footerClasses() {
    return this.data.footerClasses || '';
  }

  get hideFooter() {
    return this.data.hideFooter;
  }

  get fakeFooter() {
    return this.data.fakeFooter;
  }

  // TODO: fast decision
  get isPasswordValid() {
    if (this.data.payload.type === 'password') {
      return !this.data.payload.password;
    } else {
      return false;
    }
  }

  constructor(
    public renderer: Renderer2,
    public dialogRef: MatDialogRef<RxDialogComponent>,
    public dialogService: DialogService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
    private injector: Injector,
  ) {
    this.disableScroll = this.data.disableScroll;
  }

  ngOnInit() {
    this.hideDialogClose = this.data.hideDialogClose;
    const component = this.data.component;

    this.contentComponentRef = this.contentPlaceholder.createComponent(component, {
      injector: this.injector,
    });

    if (this.contentComponentRef.instance) {
      const instance = this.contentComponentRef.instance;
      if (typeof instance.payload === 'function') {
        this.contentComponentRef.setInput('payload', this.data.payload);
      } else {
        instance.payload = this.data.payload;
        instance.dialog = this;
      }

      if (typeof instance.init === 'function') {
        instance.init();
      }
    }

    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }
  public isFormDirty = false;

  ngAfterViewInit(): void {
    if (this.data.payload?.validateOnInit) {
      of(true)
        .pipe(delay(0), take(1))
        .subscribe(() => this.contentComponentRef.instance.validate());
    }
    const formGroup = this.contentComponentRef?.instance?.formGroup;
    if (formGroup?.valueChanges) {
      formGroup.valueChanges.subscribe(() => {
        this.cdr.markForCheck(); // ✅ triggers re-evaluation of the Save button binding
      });
    }

    if (this.data.payload?.validateOnInit) {
      of(true)
        .pipe(delay(0), take(1))
        .subscribe(() => {
          this.contentComponentRef.instance.validate();
        });
    }

    // WARNING: MIGRATION: removed empty perfect scroll
    // setTimeout(() => {
    //     this.perfectScrollBar.directiveRef.update();
    //     (this.perfectScrollBar.directiveRef as any).config.maxScrollbarLength = 100;
    //     (this.perfectScrollBar.directiveRef as any).config.scrollYMarginOffset = 10;
    //     (this.perfectScrollBar.directiveRef as any).config.suppressScrollY = false;
    //     this.perfectScrollBar.directiveRef.update();
    // }, 300);
  }

  isContentPlaceholderValid() {
    if (this.data.payload.resultRequired && this.contentComponentRef.instance.isValid) {
      return this.contentComponentRef.instance.isValid();
    }
    return true;
  }

  submitClicked() {
    const instance = this.contentComponentRef?.instance;

    if (!this.isValid) {
      instance.validate();
      return;
    }

    if (!this.data?.payload?.skipDirtyCheck && instance?.isFormDirty === false) {
      instance.snackbarService?.error?.('Please change the name before saving.');
      return;
    }

    this.dialogRef.close(instance.payload);
  }

  customClicked(preventClose: boolean) {
    this.data.payload.buttons.custom.customCallback.call(
      this,
      this.contentComponentRef.instance.payload,
    );
    if (!preventClose) {
      this.dialogRef.close();
    }
  }

  customWithoutCloseClicked() {
    this.data.payload.buttons.custom.customCallback.call(
      this,
      this.contentComponentRef.instance.payload,
    );
  }

  customSubClicked() {
    this.dialogService.onCustomClick$.next(this.contentComponentRef.instance.payload);
    if (this.data?.payload?.buttons?.customSub?.subject) {
      this.data.payload.buttons.customSub.subject.next(this.contentComponentRef.instance.payload);
    }
  }

  dangerClicked() {
    this.dialogRef.close(this.contentComponentRef.instance.payload);
  }

  dismissClicked() {
    this.dialogRef.close();
  }

  denyClicked() {
    this.dialogRef.close({ deny: true });
  }

  close() {
    this.dialogRef.close(null);
  }

  switchPage(i: number) {
    this.dialogService.pageConfig(i);
  }

  switchSize(i: number) {
    this.dialogService.sizeConfig(i);
  }

  getId(value: any) {
    return value.toString().split(' ').join('-').toLowerCase();
  }
}
